import "./ProfileIndicator.less";

import { notification, Button, Dropdown, Icon, Menu, Modal, Spin } from "antd";
import { ClickParam } from "antd/lib/menu";
import React, { useEffect, useState } from "react";
import { Redirect } from "react-router";

import { CancellationToken } from "lib/api/base";
import { User, Permission } from "lib/data/model";
import { LoginService, SessionService, StateService } from "lib/services";
import { Event, EventDispatcher } from "lib/EventDispatcher";

import { AdminPage, MySectionsPage } from "components/pages";
import { RegistrationSection } from "components/pages/AdminPage/pages";
import { LoginView } from "components/views";
import { AuthorizedComponent } from "components/general/AuthorizedComponent";
import { AdminPagePermission } from "lib/data/model/security/AdminPagePermission";
import { Right } from "lib/data/model/enumerations";
import { PermissionApi } from "lib/api";


const ProfileIndicatorFC = () => {

	const [cancellationToken] = useState(new CancellationToken());
	const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
	const [isLoginModalOpen, setLoginModalOpen] = useState<boolean>(false);
	const [loginModalTitle, setLoginModalTitle] = useState<string | undefined>();
	const [isNavigating, setNavigating] = useState<boolean>(false);
	const [destination, setDestination] = useState<string | undefined>();
	const [isProfileModalOpen, setProfileModalOpen] = useState<boolean>(false);
	const [authenticatedUser, setAuthenticatedUser] = useState<User | undefined>();
	const [isUserSaving, setUserSaving] = useState<boolean>(false);
	const [userPermissions, setUserPermissions] = useState<Permission[]>([]);

	useEffect(() => {

		const checkCurrentAuthentication = async (): Promise<boolean> => {

			const currentlyAuthenticatedUser = await LoginService.getUserIfExists();

			StateService.setCancelableState(() => setAuthenticated(currentlyAuthenticatedUser !== undefined), cancellationToken);
			StateService.setCancelableState(() => setAuthenticatedUser(currentlyAuthenticatedUser), cancellationToken);

			return currentlyAuthenticatedUser !== undefined;
		};

		const onLoggedIn = async () => {

			const isCurrentlyAuthenticated = await checkCurrentAuthentication();

			if (isCurrentlyAuthenticated) {

				StateService.setCancelableState(() => setLoginModalOpen(false), cancellationToken);
			}
		};

		const onLoggedOut = () => {

			StateService.setCancelableState(() => setAuthenticated(false), cancellationToken);
			StateService.setCancelableState(() => setAuthenticatedUser(undefined), cancellationToken);
		};

		const onUnauthorized = () => {

			StateService.setCancelableState(() => setAuthenticated(false), cancellationToken);
			StateService.setCancelableState(() => setAuthenticatedUser(undefined), cancellationToken);
		};

		EventDispatcher.register(Event.LoggedIn, onLoggedIn);
		EventDispatcher.register(Event.LoggedOut, onLoggedOut);
		EventDispatcher.register(Event.Unauthorized, onUnauthorized);

		checkCurrentAuthentication();

		PermissionApi.getUserPermissions()
			.then((queriedUserPermissions) => {

				console.log("QUERIED PERMS: ", queriedUserPermissions);

				setUserPermissions(queriedUserPermissions);
			});

		return () => {

			cancellationToken.cancel();

			EventDispatcher.unregister(Event.LoggedIn, onLoggedIn);
			EventDispatcher.unregister(Event.LoggedOut, onLoggedOut);
			EventDispatcher.unregister(Event.Unauthorized, onUnauthorized);
		};
	}, []);

	useEffect(() => {

		if (isNavigating) {

			StateService.setCancelableState(() => setNavigating(false), cancellationToken);
			StateService.setCancelableState(() => setDestination(undefined), cancellationToken);
		}
	}, [isNavigating]);

	// Check For Navigation
	if (isNavigating && destination !== undefined) {

		// Navigate To Destination
		return (
			<Redirect push={true} to={destination} />
		);
	}

	const onSaveForm = async (user: User) => {

		StateService.setCancelableState(() => setUserSaving(true), cancellationToken);

		SessionService.instance.setSessionUser(user);

		StateService.setCancelableState(() => {

			setAuthenticatedUser(user);
			setUserSaving(false);
			setProfileModalOpen(false);
		}, cancellationToken);

		notification.success({
			message: "Successfully Updated User Profile"
		});
	};

	const onLoginModalToggle = () => {

		if (!isLoginModalOpen) {

			StateService.setCancelableState(() => setLoginModalOpen(true), cancellationToken);
			document.body.style.overflow = "hidden";
		}
		else {

			StateService.setCancelableState(() => setLoginModalOpen(false), cancellationToken);
		}
	};

	const onProfileModalToggle = () => {

		if (!isProfileModalOpen) {

			StateService.setCancelableState(() => setProfileModalOpen(true), cancellationToken);
		}
		else {

			StateService.setCancelableState(() => setProfileModalOpen(false), cancellationToken);
		}
	};

	const onLoginStageChange = (title: string) => {

		// Update Modal Title
		StateService.setCancelableState(() => setLoginModalTitle(title), cancellationToken);
	};

	const onLogoutRequested = async () => {

		await LoginService.logout();
	};

	const onProfileMenuItemClick = (click: ClickParam) => {

		// Switch On Key
		switch (click.key) {

			case "my-sections":

				StateService.setCancelableState(() => {

					setNavigating(true);
					setDestination(MySectionsPage.BasePath);
				}, cancellationToken);

				break;

			case "admin":

				StateService.setCancelableState(() => {

					setNavigating(true);
					setDestination(AdminPage.BasePath);
				}, cancellationToken);

				break;

			case "edit-profile":

				StateService.setCancelableState(() => setProfileModalOpen(true), cancellationToken);

				break;

			case "logout":

				onLogoutRequested();

				break;
		}
	};

	const canNavigateAdmin = userPermissions.filter((permission) => permission.$type === AdminPagePermission.$type || permission.recordType === AdminPagePermission.$type).length > 0;

	const profileMenu = (

		<Menu onClick={onProfileMenuItemClick}>

			<Menu.Item key="my-sections">
				<Icon type="unordered-list" />
				My Sections
			</Menu.Item>

			{
				canNavigateAdmin ? (
					<Menu.Item key="admin">
						<Icon type="control" />
						Administration
					</Menu.Item>
				) : null
			}

			<Menu.Item key="edit-profile">
				<Icon type="user" />
				Edit Profile
			</Menu.Item>

			<Menu.Divider />

			<Menu.Item key="logout">
				<Icon type="logout" />
				Logout
			</Menu.Item>
		</Menu>
	);

	return (
		<div className="profile-indicator">

			{
				isAuthenticated
					?
					(
						<React.Fragment>
							{
								authenticatedUser !== undefined
									?
									(
										<Modal
											visible={isProfileModalOpen}
											onCancel={onProfileModalToggle}
											footer={null}
											title="Profile Edit"
											className="profile-indicator-profile-edit-modal"
										>
											<Spin spinning={isUserSaving}>
												<RegistrationSection
													user={authenticatedUser}
													onFormCancel={onProfileModalToggle}
													onSaveSuccess={onSaveForm}
													hidePermissionManager={true}
												/>
											</Spin>
										</Modal>
									)
									:
									null
							}

							<div className="profile-menu">
								<Dropdown
									overlay={profileMenu}
									trigger={["click"]}
								>
									<Button
										type="ghost"
										size="large"
										icon="user"
										className="profile-button"
									>
										{
											authenticatedUser
												?
												`${authenticatedUser.firstName} ${authenticatedUser.lastName}`
												:
												"Unknown"
										}
										<Icon type="down" />
									</Button>
								</Dropdown>
							</div>
						</React.Fragment>
					)
					:
					(
						<div className="profile-login">
							<Button
								icon="login"
								type="ghost"
								size="large"
								onClick={onLoginModalToggle}
							>
								Login
							</Button>
						</div>
					)
			}

			<Modal
				title={loginModalTitle}
				visible={isLoginModalOpen}
				closable={true}
				footer={null}
				onCancel={onLoginModalToggle}
				afterClose={() => {
					document.body.style.overflow = "auto";
					document.body.style.width = "auto";
				}}
			>
				<LoginView
					className="login-view-modal"
					onStageChange={onLoginStageChange}
				/>
			</Modal>
		</div>
	);
};

export class ProfileIndicator extends React.Component<any, any> {

	public render() {

		return <ProfileIndicatorFC />;
	}
}
